import { useEffect, useMemo, useState } from 'react';
import type { CwCustomerTypeAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwCustomerTypeAliases, useSetCwCustomerTypeAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwTypeName: string;
  customerTypeId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `alias-${aliasSeq}`;
}

interface CustomerTypeAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps ConnectWise company types (a multi-select) onto PSA customer-type checkboxes.
 * Each row links a CW type name to an existing PSA customer type. Matching is
 * additive on sync — existing customer-type assignments are never removed, and CW
 * types without a row here are ignored.
 */
export function CustomerTypeAliasEditor({ isOpen }: CustomerTypeAliasEditorProps): JSX.Element {
  const configQuery = useCwCustomerTypeAliases(isOpen);
  const saveAliases = useSetCwCustomerTypeAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwTypeName: alias.cwTypeName,
        customerTypeId: alias.customerTypeId,
      })),
    );
  }, [config]);

  const customerTypes = config?.customerTypes ?? [];
  const discoveredTypes = config?.discoveredTypes ?? [];
  const live = config?.live ?? false;

  // CW type names already mapped, so we can hint which discovered types remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwTypeName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredTypes.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwTypeName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwTypeName, customerTypeId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwCustomerTypeAliasDto[] = rows
      .filter((row) => row.cwTypeName.trim() && row.customerTypeId)
      .map((row) => ({ cwTypeName: row.cwTypeName.trim(), customerTypeId: row.customerTypeId }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-company-types';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Customer types (multi-select)</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise stores a company&apos;s types as a multi-select. Map each ConnectWise type to
          a PSA customer type below. On sync, matched types are <strong>added</strong> to the
          customer&apos;s type checkboxes — existing assignments are never removed, and unmapped
          ConnectWise types are ignored.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading customer types…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load customer types.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise company types loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise types discovered yet. Configure the connection to load suggestions —
              you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredTypes.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the customer-type aliases. Make sure each row targets a valid customer
              type.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No customer-type aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise type</th>
                  <th className="py-2 pr-3 font-medium">PSA customer type</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwTypeName}
                        placeholder="e.g. Client"
                        onChange={(e) => updateRow(row.id, { cwTypeName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.customerTypeId}
                        onChange={(e) => updateRow(row.id, { customerTypeId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select type —</option>
                        {customerTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save customer types
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
